import { useTranslation } from "react-i18next";
import { Control } from "react-hook-form";
import { IconButton } from "@carbon/react";
import { TrashCan } from "@carbon/icons-react";
import TextField from "../carbon/Form/TextField";
import SelectField from "../carbon/Form/SelectField";
import { BankingInformationFormFields } from "./bankingInformationValidationSchema";
import { CURRENCY_LIST } from "../../utils/enums";
import { CountryResponse } from "../../types";

export interface BankingRowProps {
  index: number;
  control: Control<BankingInformationFormFields>;
  countries: CountryResponse[];
  onRemove: () => void;
}

const ACCOUNT_TYPE_VALUES = ["SAVINGS", "CHECKING"] as const;

/**
 * One row of `BankingInformationForm`'s `useFieldArray` — same "compose `carbon/Form/*`
 * primitives per row, no new shared row primitive" pattern `AddressRow.tsx` established (Task 8).
 * `accountType`/`currency` are `SelectField` (per this task's brief); everything else is
 * `TextField`. `swift`/`iban`/`routingNumber`/`countryId` are marked `optional` (Carbon's
 * "(optional)" label-suffix convention, see `TextField`'s own docstring) since none of the four
 * is required by `bankingInfoRowValidationSchema`.
 *
 * `countryId` mirrors `AddressRow`'s own `countryId` `SelectField` exactly — same `countries`
 * prop (threaded from `usePublicCountries` by `CompanyProfileForm`/`BankingInformationForm`),
 * same `{value: country.id, label: country.name}` option shape — the only difference is this one
 * is optional rather than required, since the backend's `BankingInfoRequest.countryId` carries no
 * `@NotNull`.
 */
const BankingRow = ({ index, control, countries, onRemove }: BankingRowProps) => {
  const { t } = useTranslation();

  const accountTypeOptions = ACCOUNT_TYPE_VALUES.map((value) => ({
    value,
    label: t(
      value === "SAVINGS"
        ? "companyProfile.savingsAccount"
        : "companyProfile.checkingAccount"
    ),
  }));

  const currencyOptions = CURRENCY_LIST.map((currency) => ({
    value: currency.name,
    label: currency.name,
  }));

  const countryOptions = countries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  return (
    <div className="banking-information-form__row">
      <div className="banking-information-form__row-grid">
        <TextField
          name={`bankingInfos.${index}.bankName`}
          label={t("companyProfile.bankName")}
          control={control}
        />
        <TextField
          name={`bankingInfos.${index}.accountNumber`}
          label={t("companyProfile.accountNumber")}
          control={control}
        />
        <SelectField
          name={`bankingInfos.${index}.accountType`}
          label={t("companyProfile.accountType")}
          control={control}
          options={accountTypeOptions}
          placeholder={t("companyProfile.selectAccountType")}
        />
        <SelectField
          name={`bankingInfos.${index}.currency`}
          label={t("companyProfile.currency")}
          control={control}
          options={currencyOptions}
          placeholder={t("companyProfile.selectCurrency")}
        />
        <TextField
          name={`bankingInfos.${index}.swift`}
          label={t("companyProfile.swift")}
          control={control}
          optional
        />
        <TextField
          name={`bankingInfos.${index}.iban`}
          label={t("companyProfile.iban")}
          control={control}
          optional
          placeholder="GB82 WEST 1234 5698 7654 32"
        />
        <TextField
          name={`bankingInfos.${index}.routingNumber`}
          label={t("companyProfile.routingNumber")}
          control={control}
          optional
        />
        <SelectField
          name={`bankingInfos.${index}.countryId`}
          label={t("companyProfile.country")}
          control={control}
          options={countryOptions}
          placeholder={t("companyProfile.selectCountry")}
          optional
        />
      </div>

      <div className="banking-information-form__row-actions">
        <IconButton
          kind="ghost"
          label={t("cta.remove")}
          align="top"
          onClick={onRemove}
        >
          <TrashCan />
        </IconButton>
      </div>
    </div>
  );
};

export default BankingRow;
